import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  type RequisitionLineDto,
  type RequisitionPlacementInput,
  type StockGroupDto,
} from '@eombor/contracts';
import { Plus, ShieldCheck, X } from 'lucide-react';
import { Button, Dialog, Input, Spinner, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { stockApi } from '../../stock/api/stock.api.js';
import { stockKeys } from '../../stock/model/stock.queries.js';
import { unitText } from '../../../shared/i18n/labels.js';

/**
 * SKLADCHINING QABUL QILISH OYNASI.
 *
 * Nega u bor: talabnoma yozilayotganda partiya ham, katak ham noma'lum —
 * buyurtmachi faqat "nima va qancha" deydi. Qaysi partiyadan, qaysi katakdan
 * berilishini omborni ko'rib turgan odam biladi va aynan shu qadam qoldiqni
 * BAND qiladi. Shuning uchun manzil o'tish so'rovi bilan BIRGA ketadi
 * (`lines`) — ikki so'rovga bo'linsa, talabnoma "manzili bor, lekin qabul
 * qilinmagan" holatda qolib ketishi mumkin edi.
 *
 * BITTA SATR — BIR NECHTA KATAK. Ilgari satrga bitta manzil qo'yilardi va
 * omborda mahsulot bo'linib yotganda (3 tasi bir katakda, 1 tasi boshqasida)
 * talabnomani UMUMAN qabul qilib bo'lmasdi: hech qaysi katak yakka o'zi
 * yetmasdi, miqdorni esa kamaytirib bo'lmaydi — uni korxona belgilagan.
 * Ekran «boshqa katakni tanlang» deb maslahat berardi va bu maslahatni
 * bajarish imkonsiz edi.
 *
 * Endi oyna kataklarni O'ZI yig'adi: eng ko'p qoldiqlisidan boshlab, kerakli
 * son to'lguncha. Skladchi kerak bo'lsa o'zgartiradi, katak qo'shadi yoki
 * olib tashlaydi.
 *
 * Oyna qoldiqni O'ZI hisoblamaydi: mavjud miqdor (`availableQty`) serverdan
 * keladi, chunki formulasi (`qty - reservedQty`) bitta joyda turishi kerak.
 *
 * CHEGARA — korxona tasdiqlagan miqdor, so'ralgani EMAS. Skladchi boshliq
 * bergan sondan ko'p bera olmaydi: bu qaror uniki emas. Kataklar orasida
 * qanday taqsimlash esa — uniki, chunki omborni u ko'rib turibdi.
 */

/** Satrning YUQORI chegarasi — korxona kesgan bo'lsa o'sha, aks holda so'ralgani. */
const limitOf = (line: RequisitionLineDto): number => line.orgApprovedQty ?? line.requestedQty;

/**
 * Korxona bu satrni HAL QILGANMI.
 *
 * Hal qilingan bo'lsa YIG'INDI qat'iy: skladchi uni na oshira, na kamaytira
 * oladi. Kesilgani bilan tengi orasida farq yo'q — ikkalasida ham qaror
 * boshliqniki. Kataklar orasidagi taqsimot esa baribir tahrirlanadi.
 */
const isDecidedByOrg = (line: RequisitionLineDto): boolean =>
  line.orgApprovedQty !== null && line.orgApprovedQty !== undefined;

/** Korxona so'ralganidan KAM bergan — ogohlantirish shu holatda chiqadi. */
const isCutByOrg = (line: RequisitionLineDto): boolean =>
  isDecidedByOrg(line) && (line.orgApprovedQty as number) < line.requestedQty;

export interface AcceptDialogProps {
  open: boolean;
  onClose: () => void;
  warehouseId: string;
  lines: readonly RequisitionLineDto[];
  /** Nomenklatura nomi/kodi — kartochkada allaqachon yuklangan ma'lumotnoma. */
  nameOf: (nomenclatureId: string) => { name: string; code: string; unit?: string } | undefined;
  onConfirm: (placements: RequisitionPlacementInput[]) => void;
  pending?: boolean;
  error?: string | null;
}

/** Bitta tanlangan katak — "partiya:katak" kaliti va undan olinadigan miqdor. */
interface Pick {
  placeKey: string;
  qty: string;
}

type Place = StockGroupDto['places'][number];

const keyOf = (batchId: string, cellId: string): string => `${batchId}:${cellId}`;

/** Miqdorlar `numeric(14,3)` — yig'indi ham xuddi shunday yaxlitlanadi. */
const round3 = (value: number): number => Math.round(value * 1000) / 1000;

const qtyText = (qty: number): string => String(round3(qty));

const numberOf = (raw: string): number => Number(raw.replace(',', '.'));

/**
 * KATAKLARNI O'ZI YIG'ADI — eng ko'p qoldiqlisidan boshlab.
 *
 * Nega oyna buni o'zi qiladi: skladchi bilgan narsa — «4 ta bolt kerak».
 * Qaysi katakda nechtasi qolganini u ekrandan o'qiydi va qo'lda qo'shish
 * shunchaki hisob-kitob ishi. Eng ko'pidan boshlanadi, chunki shunda
 * kataklar soni eng kam bo'ladi — ya'ni omborda kamroq yurish.
 *
 * Yetmasa BOR qismi yig'iladi: xato keyin, yig'indi ustida aytiladi va
 * odam nima yetishmayotganini sonlar bilan ko'radi.
 */
const autoPick = (places: readonly Place[], limit: number): Pick[] => {
  const sorted = [...places].sort((a, b) => b.availableQty - a.availableQty);
  const picks: Pick[] = [];
  let left = limit;
  for (const place of sorted) {
    if (left <= 0) break;
    const take = round3(Math.min(left, place.availableQty));
    if (take <= 0) continue;
    picks.push({ placeKey: keyOf(place.batchId, place.cellId), qty: qtyText(take) });
    left = round3(left - take);
  }
  /* Hech narsa yig'ilmasa ham BITTA bo'sh qator qoladi: bo'sh ro'yxat
     «bu yerda hech narsa qilish kerak emas» degandek tuyulardi. */
  return picks.length > 0 ? picks : [{ placeKey: '', qty: '' }];
};

export const AcceptDialog = ({
  open,
  onClose,
  warehouseId,
  lines,
  nameOf,
  onConfirm,
  pending = false,
  error = null,
}: AcceptDialogProps) => {
  const [picks, setPicks] = useState<Record<string, Pick[]>>({});

  /*
   * Har satr uchun ALOHIDA so'rov: nomenklatura bo'yicha aniq filtr bilan
   * (`nomenclatureId`). Butun ombor qoldig'ini tortib olib, mijozda
   * filtrlash ham mumkin edi — lekin haqiqiy omborda u minglab qatorni
   * behuda olib kelardi va sahifalash chegarasiga urilib, kerakli mahsulot
   * ro'yxatga tushmay qolishi mumkin edi.
   */
  const results = useQueries({
    queries: lines.map((line) => {
      const query = {
        warehouseId,
        nomenclatureId: line.nomenclatureId,
        onlyAvailable: true,
        limit: 50,
      };
      return {
        queryKey: stockKeys.search(query),
        queryFn: () => stockApi.search(query),
        enabled: open,
      };
    }),
  });

  const loading = results.some((result) => result.isPending);

  /** Satr id → shu mahsulot yotgan joylar. */
  const placesOf = useMemo(() => {
    const map = new Map<string, Place[]>();
    lines.forEach((line, index) => {
      const page = results[index]?.data;
      const group = page?.items.find((item) => item.nomenclatureId === line.nomenclatureId);
      map.set(line.id, group?.places ?? []);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `results` har chizishda yangi massiv
  }, [lines, results.map((r) => r.dataUpdatedAt).join(',')]);

  /*
   * Oyna ochilganda tanlov TAYYOR holatda boshlanadi. Mahsulot bitta joyda
   * yotgan bo'lsa — bitta qator; bo'lingan bo'lsa — kerakli son to'lguncha
   * bir nechta. Skladchi odatda hech narsa o'zgartirmaydi va shunchaki
   * tasdiqlaydi.
   */
  useEffect(() => {
    if (!open || loading) return;
    setPicks((prev) => {
      const next: Record<string, Pick[]> = {};
      for (const line of lines) {
        next[line.id] = prev[line.id] ?? autoPick(placesOf.get(line.id) ?? [], limitOf(line));
      }
      return next;
    });
  }, [open, loading, lines, placesOf]);

  const rows = lines.map((line) => {
    const places = placesOf.get(line.id) ?? [];
    const chosen = picks[line.id] ?? [{ placeKey: '', qty: '' }];
    const limit = limitOf(line);
    const info = nameOf(line.nomenclatureId);
    const unit = info?.unit === undefined ? '' : unitText(info.unit as never);

    /* Omborda JAMI qancha bor — yetmaslik xabari shu songa tayanadi.
       «Bu katakda 3 bor» deyish yordam bermaydi: odam qaysi katak yetadi
       deb izlab yurardi, holbuki hech qaysisi yetmaydi. */
    const stock = round3(places.reduce((sum, place) => sum + place.availableQty, 0));

    const seen = new Set<string>();
    const parts = chosen.map((pick) => {
      const place = places.find((p) => keyOf(p.batchId, p.cellId) === pick.placeKey);
      const qty = numberOf(pick.qty);
      const duplicate = pick.placeKey !== '' && seen.has(pick.placeKey);
      seen.add(pick.placeKey);

      const problem =
        place === undefined
          ? t('requisition.acceptPickPlace')
          : duplicate
            ? t('requisition.acceptDuplicatePlace')
            : !Number.isFinite(qty) || qty <= 0
              ? t('requisition.acceptQtyInvalid')
              : qty > place.availableQty
                ? t('requisition.acceptQtyTooBig', { max: qtyText(place.availableQty) })
                : null;

      return { pick, place, qty, problem };
    });

    const total = round3(
      parts.reduce((sum, part) => sum + (Number.isFinite(part.qty) ? part.qty : 0), 0),
    );

    /*
     * SATR muammosi — qatorlarnikidan KEYIN va faqat ular toza bo'lganda.
     *
     * Tartib muhim: yig'indi noto'g'ri bo'lishining sababi ko'pincha
     * qatorning o'zida (katak tanlanmagan, miqdor bo'sh) va o'shanda
     * «yig'indi 4 bo'lishi kerak» degan xabar ikkinchi, chalg'ituvchi
     * gap bo'lardi.
     */
    const rowProblem =
      places.length === 0
        ? t('requisition.acceptNoStock')
        : parts.some((part) => part.problem !== null)
          ? ''
          : stock < limit
            ? t('requisition.acceptShortTotal', {
                have: `${qtyText(stock)}${unit === '' ? '' : ` ${unit}`}`,
                need: `${qtyText(limit)}${unit === '' ? '' : ` ${unit}`}`,
              })
            : isDecidedByOrg(line) && total !== round3(limit)
              ? t('requisition.acceptSumMismatch', { need: qtyText(limit) })
              : total > limit
                ? t('requisition.acceptOverRequested', { max: qtyText(limit) })
                : null;

    const blocked = rowProblem !== null || parts.some((part) => part.problem !== null);

    /* Qolgan kataklar — «yana qo'shish» faqat tanlanmagani bo'lsa ma'noli. */
    const spare = places.some((place) => !seen.has(keyOf(place.batchId, place.cellId)));

    return { line, places, parts, info, unit, limit, total, stock, rowProblem, blocked, spare };
  });

  const blocked = rows.filter((row) => row.blocked).length;
  const ready = blocked === 0 && !loading && rows.length > 0;

  const setLine = (lineId: string, next: Pick[]): void =>
    setPicks((prev) => ({ ...prev, [lineId]: next }));

  const confirm = (): void => {
    onConfirm(
      rows.flatMap((row) =>
        row.parts.flatMap((part) =>
          part.place === undefined
            ? []
            : [
                {
                  lineId: row.line.id,
                  batchId: part.place.batchId,
                  cellId: part.place.cellId,
                  qty: part.qty,
                },
              ],
        ),
      ),
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      /* Chizma emas, lekin har satrda bir nechta qator bo'lishi mumkin —
         `lg` da katak tanlagich bilan miqdor maydoni bir-biriga tiqilardi. */
      size="xl"
      title={t('requisition.acceptTitle')}
      footer={
        <>
          {/*
            Tugma nega o'chiqligi KO'RINIB turadi. Bu yerda satrning xato
            matni TAKRORLANMAYDI — u o'z maydonida allaqachon yozilgan;
            pastda esa SANOQ turadi, chunki uzun ro'yxatda muammoli satr
            ekrandan chiqib ketgan bo'lishi mumkin.
          */}
          {blocked === 0 ? null : (
            <span className="mr-auto text-sm text-text-muted">
              {t('requisition.acceptBlocked', { count: blocked })}
            </span>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t('requisition.cancel')}
          </Button>
          <Button loading={pending} disabled={!ready} onClick={confirm}>
            {t('requisition.acceptConfirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-muted">
          {t('requisition.acceptHint')} {t('requisition.acceptSplitHint')}
        </p>

        {error === null ? null : (
          <p
            role="alert"
            className={cn(
              'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft',
              'px-3 py-2 text-sm text-danger',
            )}
          >
            {error}
          </p>
        )}

        {loading ? (
          <p className="flex items-center gap-2 text-sm text-text-muted">
            <Spinner size="sm" />
            {t('requisition.acceptLoading')}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {rows.map((row) => (
              <li
                key={row.line.id}
                className="flex flex-col gap-3 rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3"
              >
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="min-w-0 truncate text-sm font-medium text-text">
                      {row.info?.name ?? `#${row.line.nomenclatureId}`}
                    </span>
                    <span className="tabular shrink-0 text-sm text-text-muted">
                      {t('requisition.acceptRequested')}: {qtyText(row.line.requestedQty)}
                      {row.unit === '' ? '' : ` ${row.unit}`}
                    </span>
                  </div>

                  {/*
                    KORXONA KESGAN bo'lsa — buni skladchi ko'rishi shart.
                    Ilgari oynada faqat so'ralgan miqdor turardi va skladchi
                    23 ta qo'yardi, holbuki boshliq 10 ta bergan edi. Sabab
                    ham shu yerda: "nega 10?" degan savolga javob hujjatda
                    bor va uni qidirib yurish kerak emas.
                  */}
                  {!isCutByOrg(row.line) ? null : (
                    <div
                      className={cn(
                        'flex flex-col gap-1 rounded-[var(--radius-sm)] border',
                        'border-warn/25 bg-warn-soft px-2.5 py-2',
                      )}
                    >
                      <span className="flex items-center gap-1.5 text-xs font-medium text-warn">
                        <ShieldCheck size={13} aria-hidden className="shrink-0" />
                        {t('requisition.acceptOrgLimit', {
                          qty: qtyText(row.limit),
                          unit: row.unit,
                        })}
                      </span>
                      {row.line.orgNote === null ? null : (
                        <span className="text-xs leading-5 text-text-muted">
                          {row.line.orgNote}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {row.places.length === 0 ? (
                  <p className="text-sm text-warn">{t('requisition.acceptNoStockLong')}</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {row.parts.map((part, index) => (
                      <div
                        key={index}
                        className="grid items-start gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]"
                      >
                        <Select
                          label={t('requisition.acceptPlaceLabel')}
                          hiddenLabel={index > 0}
                          value={part.pick.placeKey}
                          placeholder={t('requisition.acceptPickPlace')}
                          options={row.places.map((place) => ({
                            value: keyOf(place.batchId, place.cellId),
                            label: `${place.cellCode} · ${place.batchNo} · ${qtyText(place.availableQty)}`,
                          }))}
                          onChange={(event) =>
                            setLine(
                              row.line.id,
                              row.parts.map((other, i) =>
                                i === index
                                  ? { ...other.pick, placeKey: event.target.value }
                                  : other.pick,
                              ),
                            )
                          }
                        />

                        {/*
                          Miqdor har katakda TAHRIRLANADI — korxona hal
                          qilgan satrda ham. Boshliq YIG'INDINI belgilagan,
                          kataklar orasidagi taqsimotni esa omborni ko'rib
                          turgan odam biladi. Ilgari maydon qulflangan edi
                          va aynan shu narsa bo'lingan qoldiqni qabul
                          qilishni imkonsiz qilardi.
                        */}
                        <Input
                          /* Yorliq FAQAT birinchi qatorda: pastdagilarda u
                             takrorlanib, ustun uch marta bir xil so'z
                             bo'lib turardi. Ekran o'qigich uchun esa
                             `aria-label` qoladi. */
                          {...(index === 0
                            ? { label: t('requisition.acceptQtyLabel') }
                            : { 'aria-label': t('requisition.acceptQtyLabel') })}
                          inputMode="decimal"
                          value={part.pick.qty}
                          onChange={(event) =>
                            setLine(
                              row.line.id,
                              row.parts.map((other, i) =>
                                i === index
                                  ? { ...other.pick, qty: event.target.value }
                                  : other.pick,
                              ),
                            )
                          }
                          {...(part.problem === null
                            ? {
                                hint:
                                  part.place === undefined
                                    ? undefined
                                    : t('requisition.acceptInCell', {
                                        qty: qtyText(part.place.availableQty),
                                      }),
                              }
                            : { error: part.problem })}
                        />

                        {/* Bitta qator qolganda × YO'Q: uni bosgan odam
                            satrni manzilsiz qoldirardi va oyna darrov
                            «katak tanlang» deb qaytarardi. */}
                        {row.parts.length < 2 ? (
                          <span className="hidden sm:block sm:w-9" aria-hidden />
                        ) : (
                          <Button
                            variant="ghost"
                            aria-label={t('requisition.acceptRemovePlace', {
                              cell: part.place?.cellCode ?? '',
                            })}
                            title={t('requisition.acceptRemovePlace', {
                              cell: part.place?.cellCode ?? '',
                            })}
                            onClick={() =>
                              setLine(
                                row.line.id,
                                row.parts.filter((_, i) => i !== index).map((other) => other.pick),
                              )
                            }
                            className={cn('px-0! w-9 shrink-0', index === 0 ? 'sm:mt-6' : '')}
                          >
                            <X size={15} aria-hidden />
                          </Button>
                        )}
                      </div>
                    ))}

                    <div className="flex flex-wrap items-center justify-between gap-2">
                      {/* «Yana katak» faqat tanlanmagan katak qolganda:
                          bo'lmasa u bosilib, darrov «katak tanlang» degan
                          xato qatorini qo'shardi. */}
                      {!row.spare ? (
                        <span />
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setLine(row.line.id, [
                              ...row.parts.map((other) => other.pick),
                              { placeKey: '', qty: '' },
                            ])
                          }
                        >
                          <Plus size={14} aria-hidden />
                          {t('requisition.acceptAddPlace')}
                        </Button>
                      )}

                      {/* YIG'INDI — «4 / 4» . Bitta katakli satrda ham
                          turadi: qoida bitta bo'lsa, o'qish ham bitta. */}
                      <span
                        className={cn(
                          'tabular text-sm',
                          row.total === round3(row.limit) ? 'text-success' : 'text-text-muted',
                        )}
                      >
                        {t('requisition.acceptTotalLine', {
                          total: qtyText(row.total),
                          need: qtyText(row.limit),
                        })}
                      </span>
                    </div>

                    {row.rowProblem === null || row.rowProblem === '' ? null : (
                      <p role="alert" className="text-sm text-danger">
                        {row.rowProblem}
                      </p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
};
